import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ErrorBanner from './ErrorBanner';
import { drainRecentFailures, clearRecentFailures } from '../../services/api/client';
import type { ApiFailureDetail } from '../../services/api/client';

/**
 * ApiFailureBanner — the app-level guarantee that a backend outage can never
 * render as data.
 *
 * This is wiring, not a new widget: it listens for the `api:request-failed`
 * signal the API client broadcasts (see services/api/client.ts) and renders the
 * existing Common/ErrorBanner. Nothing here duplicates DataState / ErrorBanner /
 * FallbackBanner — those are per-panel affordances, and each of them was built
 * in an earlier round and then adopted by almost nobody (DataState: 2 files,
 * useApiData: 1 file). Per-panel adoption is still the right end state; this is
 * the floor underneath it, so that "nobody remembered to handle the error"
 * degrades to a visible banner instead of a confident zero.
 *
 * Measured before this existed, with every /api/ request forced to 500: the
 * overwhelming majority of routes rendered numbers, empty tables and zeros with
 * no indication anything had failed.
 *
 * Scoping rules that matter:
 * - Cleared on navigation. A failure on /inventory must not accuse /settings.
 * - Counts failures so a dashboard fanning out 30 dead calls says "12 requests
 *   failed", not just the last one. (The client debounces the *event* to one
 *   per 1.5s; this counts what does arrive.)
 * - Dismissible, and it does not block the page — whatever data DID load stays
 *   on screen and usable, which is the Wiz behaviour: degrade, label, continue.
 */
const ApiFailureBanner: React.FC = () => {
  const location = useLocation();
  const [failure, setFailure] = useState<ApiFailureDetail | null>(null);
  const [count, setCount] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const dismissedFor = useRef<string | null>(null);

  // Reset on route change — including the dismissal, so dismissing the banner
  // on one page does not hide a real outage on the next. The client's buffer is
  // cleared too: the new route must be judged on its own requests.
  useEffect(() => {
    setFailure(null);
    setCount(0);
    setRetrying(false);
    dismissedFor.current = null;
    clearRecentFailures();
  }, [location.pathname]);

  useEffect(() => {
    // Catch up on failures raised before this listener attached. /compliance
    // fans out 82 requests on mount, and its first failures were landing before
    // the listener existed — measured as the page still lying while every other
    // route had been fixed.
    const missed = drainRecentFailures();
    if (missed.length) {
      setFailure(missed[missed.length - 1]);
      setCount(missed.length);
    }
    const onFail = (e: Event) => {
      const detail = (e as CustomEvent<ApiFailureDetail>).detail;
      if (!detail) return;
      if (dismissedFor.current === location.pathname) return;
      setFailure(detail);
      setCount(c => c + 1);
    };
    window.addEventListener('api:request-failed', onFail);
    return () => window.removeEventListener('api:request-failed', onFail);
  }, [location.pathname]);

  const onRetry = useCallback(() => {
    // The client owns no cache and panels own their own fetches, so there is no
    // honest way to re-drive every in-page loader from here. Reloading the route
    // is the truthful option: it re-runs exactly what failed.
    setRetrying(true);
    window.location.reload();
  }, []);

  if (!failure) return null;

  const scope = count > 1 ? `${count} requests failed` : 'A request failed';
  const reason =
    failure.status === 0 ? 'the server could not be reached'
      : failure.status === 408 ? 'the request timed out'
        : failure.status === 503 ? 'the service is unavailable'
          : `the server returned ${failure.status}`;

  return (
    <div className="px-6 pt-4">
      <ErrorBanner
        message={`${scope} — ${reason}. Anything below may be incomplete or missing.`}
        onRetry={onRetry}
        retrying={retrying}
      />
    </div>
  );
};

export default ApiFailureBanner;
